import React from 'react';
import {
  TriggerEmailRuleKind as Kind,
  TriggerEmailPhraseField as PhraseField,
  TriggerEmailRecipientField as RecipientField,
  TriggerEmailTextOperator as TextOperator,
  TriggerEmailAddressKind as AddressKind,
  TriggerEmailTimeOperator as TimeOperator,
  type TriggerEmailRuleInput
} from '~/api/graphql';
import {
  RULE_PATH,
  countNodes,
  countPrompts,
  isGroup,
  newRule,
  MAX_NODES
} from '~/api/data/triggers/config';
import {
  EMAIL_LIMITS,
  type EmailErrors as BuilderErrors,
  type EmailOptions as EmailBuilderOptions
} from '~/api/data/triggers/email-config';
import { ConditionEditor, minuteText } from '~/components/triggers/email-builder/condition-editors';
import { PathErrors, Help } from '~/components/triggers/email-builder/fields';
import { RuleEditor } from '~/components/triggers/email-builder/rule-editor';
import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import { Input } from '@oysterjs/ui/input';
import { Field, Label } from '@oysterjs/ui/fieldset';
import { Select } from '@oysterjs/ui/select';
import { SegmentedControl, SegmentedControlOption } from '@oysterjs/ui/segmented-control';
import {
  AdjustmentsHorizontalIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon
} from '@heroicons/react/16/solid';

// Creation-only "If" card. It shows the existing rule tree as the design's
// groups of Field | Operator | Value rows. "Does not ..." operators are a NOT
// node around the condition; every other setting stays in the existing
// ConditionEditor behind the row's settings button. Nothing is converted.

type Rule = TriggerEmailRuleInput;
type Mode = Kind.And | Kind.Or;
type Row = { node: Rule; path: string; leaf: Rule; leafPath: string; negated: boolean };
type Group = { mode: Mode; rows: Row[]; path: string; node: Rule | null };
type View =
  | { type: 'empty' }
  | { type: 'single'; group: Group }
  | { type: 'multi'; join: Mode; groups: Group[] }
  | { type: 'advanced' };

const isMode = (kind: Kind): kind is Mode => kind === Kind.And || kind === Kind.Or;
const isRow = (node: Rule) =>
  node.kind === Kind.Not
    ? node.children?.length === 1 && !isGroup(node.children[0].kind)
    : !isGroup(node.kind);
const rowOf = (node: Rule, path: string): Row =>
  node.kind === Kind.Not
    ? { node, path, leaf: node.children![0], leafPath: `${path}.children.0`, negated: true }
    : { node, path, leaf: node, leafPath: path, negated: false };
const flatGroup = (node: Rule, path: string): Group | null =>
  isMode(node.kind) && (node.children ?? []).every(isRow)
    ? {
        mode: node.kind,
        rows: (node.children ?? []).map((child, i) => rowOf(child, `${path}.children.${i}`)),
        path,
        node
      }
    : null;
const viewOf = (rule: Rule | null | undefined, mode: Mode): View => {
  if (!rule) return { type: 'empty' };
  if (isRow(rule))
    return {
      type: 'single',
      group: { mode, rows: [rowOf(rule, RULE_PATH)], path: RULE_PATH, node: null }
    };
  const single = flatGroup(rule, RULE_PATH);
  if (single) return { type: 'single', group: single };
  const groups = isMode(rule.kind)
    ? (rule.children ?? []).map((child, i) => flatGroup(child, `${RULE_PATH}.children.${i}`))
    : [];
  if (groups.length && groups.every((group) => group))
    return { type: 'multi', join: rule.kind as Mode, groups: groups as Group[] };
  return { type: 'advanced' };
};
const group = (mode: Mode, children: Rule[]): Rule => ({ ...newRule(mode), children });
const errorKeys = (errors: BuilderErrors, path: string) =>
  Object.keys(errors).filter((key) => key === path || key.startsWith(`${path}.`));

// Field: what the condition looks at. Sender address type and recipient field
// are separate design fields; the kind's own settings keep their values.
const FIELDS = [
  ['subject', 'Subject'],
  ['body', 'Email body'],
  ['sender-email', 'Sender email'],
  ['sender-domain', 'Sender domain'],
  ['sender-user', 'Sender agency user'],
  ['sender-profile', 'Sender access profile'],
  ['to', 'To'],
  ['cc', 'Cc'],
  ['time', 'Time received'],
  ['attachments', 'Attachments'],
  ['prompt', 'Define Condition via Prompt']
] as const;
// Saved conditions can still use these; they stay readable and editable, but a new
// condition expresses them with another field plus the operator, checkbox or a second condition.
const LEGACY_FIELDS = [
  ['subject-or-body', 'Subject or body (saved)'],
  ['to-or-cc', 'To or Cc (saved)']
] as const;
type FieldKey = (typeof FIELDS)[number][0] | (typeof LEGACY_FIELDS)[number][0];
const PHRASE_FIELDS: Partial<Record<FieldKey, PhraseField>> = {
  subject: PhraseField.Subject,
  body: PhraseField.Body,
  'subject-or-body': PhraseField.SubjectOrBody
};
const SENDER_FIELDS: Partial<Record<FieldKey, AddressKind>> = {
  'sender-email': AddressKind.Address,
  'sender-domain': AddressKind.Domain,
  'sender-user': AddressKind.User,
  'sender-profile': AddressKind.AccessProfile
};
const RECIPIENT_FIELDS: Partial<Record<FieldKey, RecipientField>> = {
  to: RecipientField.To,
  cc: RecipientField.Cc,
  'to-or-cc': RecipientField.ToOrCc
};
const kindOf = (key: FieldKey): Kind =>
  PHRASE_FIELDS[key]
    ? Kind.Phrase
    : SENDER_FIELDS[key]
      ? Kind.Sender
      : RECIPIENT_FIELDS[key]
        ? Kind.Recipient
        : { time: Kind.Time, attachments: Kind.Attachment, prompt: Kind.Prompt }[
            key as 'time' | 'attachments' | 'prompt'
          ];
const find = <T,>(map: Partial<Record<FieldKey, T>>, value?: T) =>
  (Object.keys(map) as FieldKey[]).find((key) => map[key] === value);
const fieldOf = (leaf: Rule): FieldKey | undefined => {
  if (leaf.kind === Kind.Phrase) return find(PHRASE_FIELDS, leaf.phrase?.field);
  if (leaf.kind === Kind.Sender) return find(SENDER_FIELDS, leaf.sender?.kind);
  if (leaf.kind === Kind.Recipient) return find(RECIPIENT_FIELDS, leaf.recipient?.field);
  return ({ TIME: 'time', ATTACHMENT: 'attachments', PROMPT: 'prompt' } as const)[
    leaf.kind as 'TIME' | 'ATTACHMENT' | 'PROMPT'
  ];
};
const withField = (leaf: Rule, key: FieldKey): Rule => {
  const kind = kindOf(key);
  const base = kind === leaf.kind ? leaf : { ...newRule(kind), id: leaf.id };
  if (base.phrase && PHRASE_FIELDS[key])
    return { ...base, phrase: { ...base.phrase, field: PHRASE_FIELDS[key]! } };
  if (base.sender && SENDER_FIELDS[key])
    return {
      ...base,
      sender: {
        ...base.sender,
        kind: SENDER_FIELDS[key]!,
        includeSubdomains:
          base.sender.kind === SENDER_FIELDS[key] ? base.sender.includeSubdomains : false,
        values: base.sender.kind === SENDER_FIELDS[key] ? base.sender.values : []
      }
    };
  if (base.recipient && RECIPIENT_FIELDS[key])
    return { ...base, recipient: { ...base.recipient, field: RECIPIENT_FIELDS[key]! } };
  return base;
};

// Operator: the kind's own operator plus whether a NOT node wraps it.
type Operator = { key: string; label: string; negated: boolean; apply: (leaf: Rule) => Rule };
const pair = (
  key: string,
  label: string,
  negatedLabel: string,
  apply: (leaf: Rule) => Rule = (leaf) => leaf
): Operator[] => [
  { key, label, negated: false, apply },
  { key: `not:${key}`, label: negatedLabel, negated: true, apply }
];
const operatorsOf = (leaf: Rule): Operator[] => {
  switch (leaf.kind) {
    case Kind.Phrase:
      return (
        [
          [TextOperator.Contains, 'Contains', 'Does not contain'],
          [TextOperator.Equals, 'Equals', 'Does Not Equal to'],
          [TextOperator.StartsWith, 'Starts with', 'Does not start with'],
          [TextOperator.EndsWith, 'Ends with', 'Does not end with'],
          [TextOperator.Regex, 'Matches regex', 'Does not match regex']
        ] as const
      ).flatMap(([operator, label, negatedLabel]) =>
        pair(operator, label, negatedLabel, (item) => ({
          ...item,
          phrase: { ...item.phrase!, match: { ...item.phrase!.match, operator } }
        }))
      );
    case Kind.Sender:
      return pair('is', 'Equals', 'Does Not Equal to');
    case Kind.Recipient:
      return (
        [
          [AddressKind.Address, 'Includes email', 'Does not include email'],
          [AddressKind.Domain, 'Includes domain', 'Does not include domain'],
          [AddressKind.User, 'Includes user', 'Does not include user'],
          [AddressKind.AccessProfile, 'Includes access profile', 'Does not include access profile']
        ] as const
      ).flatMap(([kind, label, negatedLabel]) =>
        pair(kind, label, negatedLabel, (item) =>
          item.recipient!.match.kind === kind
            ? item
            : {
                ...item,
                recipient: {
                  ...item.recipient!,
                  match: { ...item.recipient!.match, kind, values: [], includeSubdomains: false }
                }
              }
        )
      );
    case Kind.Time:
      return (
        [
          [TimeOperator.Between, 'Is between', 'Is not between'],
          [TimeOperator.Before, 'Is before', 'Is not before'],
          [TimeOperator.After, 'Is after', 'Is not after']
        ] as const
      ).flatMap(([operator, label, negatedLabel]) =>
        pair(operator, label, negatedLabel, (item) =>
          item.time!.operator === operator
            ? item
            : {
                ...item,
                time: {
                  ...item.time!,
                  operator,
                  endMinute:
                    operator === TimeOperator.Between ? (item.time!.endMinute ?? null) : null
                }
              }
        )
      );
    case Kind.Attachment:
      return pair('count', 'Has at least', 'Has fewer than');
    case Kind.Prompt:
      return pair('yes', 'Answer is yes', 'Answer is no');
    default:
      return [];
  }
};
const operatorOf = (row: Row) => {
  const { leaf } = row;
  const key =
    leaf.kind === Kind.Phrase
      ? leaf.phrase?.match.operator
      : leaf.kind === Kind.Recipient
        ? leaf.recipient?.match.kind
        : leaf.kind === Kind.Time
          ? leaf.time?.operator
          : { SENDER: 'is', ATTACHMENT: 'count', PROMPT: 'yes' }[
              leaf.kind as 'SENDER' | 'ATTACHMENT' | 'PROMPT'
            ];
  return row.negated ? `not:${key}` : `${key}`;
};
const withLeaf = (row: Row, leaf: Rule, negated = row.negated): Rule =>
  !negated
    ? leaf
    : row.negated
      ? { ...row.node, children: [leaf] }
      : { ...newRule(Kind.Not), children: [leaf] };

const fromTime = (value: string) =>
  value ? Number(value.slice(0, 2)) * 60 + Number(value.slice(3, 5)) : -1;
// Sender operators and recipient kinds share the ADDRESS and DOMAIN values.
const PLACEHOLDERS: Partial<Record<AddressKind, string>> = {
  [AddressKind.Address]: 'name@example.com',
  [AddressKind.Domain]: 'example.com'
};

const ValueControl = ({
  row,
  options,
  invalid,
  onChange,
  onOpenSettings
}: {
  row: Row;
  options: EmailBuilderOptions;
  invalid: (suffix: string) => boolean;
  onChange: (leaf: Rule) => void;
  onOpenSettings: () => void;
}) => {
  const { leaf } = row;
  const summary = (text: string, suffix: string) => (
    <Button
      outline
      className="min-w-0 flex-1 justify-start truncate"
      aria-label={`Value: ${text}. Open condition settings`}
      aria-invalid={invalid(suffix) || undefined}
      onClick={onOpenSettings}
    >
      {text}
    </Button>
  );
  // A single value edits in place. Several values are never joined into one
  // string; they open the existing list editor instead.
  const text = (
    values: string[],
    suffix: string,
    placeholder: string,
    set: (values: string[]) => void
  ) =>
    values.length > 1 || values.some((value) => /[\r\n]/.test(value)) ? (
      summary(
        values.length > 1
          ? `${values[0]} + ${values.length - 1} more`
          : 'Multi-line text (edit in advanced)',
        suffix
      )
    ) : (
      <Input
        type="text"
        className="min-w-0 flex-1"
        aria-label="Value"
        invalid={invalid(suffix)}
        placeholder={placeholder}
        value={values[0] ?? ''}
        onChange={(event) => set([event.target.value])}
      />
    );
  if (leaf.phrase) {
    const match = leaf.phrase.match;
    return text(
      match.values,
      '.phrase.match',
      match.operator === TextOperator.Regex ? 'Pattern (regex)' : 'Text to look for',
      (values) => onChange({ ...leaf, phrase: { ...leaf.phrase!, match: { ...match, values } } })
    );
  }
  if (leaf.sender || leaf.recipient) {
    const value = leaf.sender ?? leaf.recipient!.match;
    const suffix = leaf.sender ? '.sender' : '.recipient.match';
    if (value.kind === AddressKind.User || value.kind === AddressKind.AccessProfile) {
      const names = value.values.map(
        (id) =>
          (value.kind === AddressKind.User
            ? options.users.find((user) => user.id === id)?.name
            : options.profiles.find((profile) => profile.id === id)?.name) ??
          'Unavailable selection'
      );
      return summary(
        names.length
          ? names.length > 1
            ? `${names[0]} + ${names.length - 1} more`
            : names[0]
          : value.kind === AddressKind.User
            ? 'Choose users'
            : 'Choose access profiles',
        suffix
      );
    }
    return text(value.values, suffix, PLACEHOLDERS[value.kind] ?? '', (values) =>
      onChange(
        leaf.sender
          ? { ...leaf, sender: { ...value, values } }
          : { ...leaf, recipient: { ...leaf.recipient!, match: { ...value, values } } }
      )
    );
  }
  if (leaf.time) {
    const value = leaf.time;
    const time = (
      label: string,
      minute: number | null | undefined,
      key: 'minute' | 'endMinute'
    ) => (
      <Input
        type="time"
        className="w-auto"
        aria-label={label}
        invalid={invalid(`.time.${key}`)}
        value={minuteText(minute ?? -1)}
        onChange={(event) =>
          onChange({ ...leaf, time: { ...value, [key]: fromTime(event.target.value) } })
        }
      />
    );
    return (
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {time(
          value.operator === TimeOperator.Between ? 'Start time' : 'Time',
          value.minute,
          'minute'
        )}
        {value.operator === TimeOperator.Between && (
          <>
            <span aria-hidden="true" className="text-sm text-neutral-500 dark:text-neutral-400">
              and
            </span>
            {value.endMinute === 1440 ? (
              <Button
                outline
                aria-label="End time: 24:00. Open condition settings"
                onClick={onOpenSettings}
              >
                24:00
              </Button>
            ) : (
              time('End time', value.endMinute, 'endMinute')
            )}
          </>
        )}
      </div>
    );
  }
  if (leaf.attachment) {
    const value = leaf.attachment;
    return (
      <Input
        type="number"
        min={0}
        max={2147483647}
        step={1}
        inputMode="numeric"
        className="w-28"
        aria-label="Number of matching attachments"
        invalid={invalid('.attachment.minimumCount')}
        value={value.minimumCount === -1 ? '' : (value.minimumCount ?? '')}
        onChange={(event) =>
          onChange({
            ...leaf,
            attachment: {
              ...value,
              minimumCount: event.target.value === '' ? -1 : Number(event.target.value)
            }
          })
        }
      />
    );
  }
  if (leaf.prompt)
    return /[\r\n]/.test(leaf.prompt.prompt) ? (
      summary('Multiline matching instruction (edit in settings)', '.prompt')
    ) : (
      <Input
        type="text"
        className="min-w-0 flex-1"
        aria-label="Question"
        invalid={invalid('.prompt')}
        placeholder="Write a matching instruction"
        value={leaf.prompt.prompt}
        onChange={(event) => onChange({ ...leaf, prompt: { prompt: event.target.value } })}
      />
    );
  return null;
};

const Condition = ({
  row,
  options,
  errors,
  canNegate,
  canAddPrompt,
  onChange,
  onRemove
}: {
  row: Row;
  options: EmailBuilderOptions;
  errors: BuilderErrors;
  canNegate: boolean;
  canAddPrompt: boolean;
  onChange: (node: Rule) => void;
  onRemove: () => void;
}) => {
  const [open, setOpen] = React.useState(false);
  const settingsId = React.useId();
  const { leaf, leafPath } = row;
  // Detail errors open the full settings so the message is always visible.
  const detailErrors = errorKeys(errors, leafPath).filter(
    (key) => ![leafPath, `${leafPath}.id`, `${leafPath}.kind`].includes(key)
  );
  const expanded = open || detailErrors.length > 0;
  const invalid = (suffix: string) =>
    detailErrors.some((key) => key.startsWith(`${leafPath}${suffix}`));
  const field = fieldOf(leaf);
  const operators = operatorsOf(leaf);
  const operator = operatorOf(row);
  const fieldSelect = (labelled: boolean) => (
    <Select
      className={labelled ? undefined : 'w-44 sm:w-auto sm:min-w-40'}
      aria-label={labelled ? undefined : 'Field'}
      value={field ?? ''}
      onChange={(event) => {
        const next = withField(leaf, event.target.value as FieldKey);
        onChange(withLeaf(row, next));
      }}
    >
      {!field && <option value="">Unsupported condition</option>}
      {[...FIELDS, ...LEGACY_FIELDS.filter(([key]) => key === field)].map(([key, label]) => (
        <option
          key={key}
          value={key}
          disabled={key === 'prompt' && leaf.kind !== Kind.Prompt && !canAddPrompt}
        >
          {label}
        </option>
      ))}
    </Select>
  );
  const operatorSelect = (labelled: boolean) => (
    <Select
      className={labelled ? undefined : 'w-44 sm:w-auto sm:min-w-40'}
      aria-label={labelled ? undefined : 'Operator'}
      value={operator}
      onChange={(event) => {
        const next = operators.find((item) => item.key === event.target.value)!;
        onChange(withLeaf(row, next.apply(leaf), next.negated));
      }}
    >
      {operators.map((item) => (
        <option
          key={item.key}
          value={item.key}
          disabled={item.negated && !row.negated && !canNegate}
        >
          {item.label}
        </option>
      ))}
    </Select>
  );
  return (
    <div data-rule-id={leaf.id} data-rule-kind={leaf.kind}>
      <div className="flex flex-wrap items-start gap-2">
        {expanded ? (
          <div
            id={settingsId}
            className="min-w-0 flex-1 basis-80 space-y-6 rounded-lg border border-neutral-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-800"
            role="group"
            aria-label="Condition settings"
          >
            <div className="max-w-xl space-y-6">
              <Field>
                <Label>Field</Label>
                {fieldSelect(true)}
              </Field>
              <Field>
                <Label>Operator</Label>
                {operatorSelect(true)}
              </Field>
              <ConditionEditor
                compact
                node={leaf}
                path={leafPath}
                options={options}
                errors={errors}
                onChange={(next) => onChange(withLeaf(row, next))}
              />
            </div>
          </div>
        ) : (
          <>
            {fieldSelect(false)}
            {operatorSelect(false)}
            <ValueControl
              row={row}
              options={options}
              invalid={invalid}
              onChange={(next) => onChange(withLeaf(row, next))}
              onOpenSettings={() => setOpen(true)}
            />
          </>
        )}
        {!expanded && (
          <Button
            plain
            aria-label="Use advanced condition"
            aria-controls={settingsId}
            onClick={() => setOpen(true)}
          >
            <AdjustmentsHorizontalIcon />
          </Button>
        )}
        <Button plain aria-label="Remove condition" onClick={onRemove}>
          <XMarkIcon />
        </Button>
      </div>
      <PathErrors path={row.path} errors={errors} />
      {row.negated && <PathErrors path={leafPath} errors={errors} />}
    </div>
  );
};

const Segments = ({
  label,
  value,
  onChange
}: {
  label: string;
  value: Mode;
  onChange: (mode: Mode) => void;
}) => (
  <div className="w-36">
    <SegmentedControl aria-label={label} value={value} onChange={onChange}>
      <SegmentedControlOption value={Kind.And}>All</SegmentedControlOption>
      <SegmentedControlOption value={Kind.Or}>Any</SegmentedControlOption>
    </SegmentedControl>
  </div>
);

/** Card-heading control shown only while there are several groups. */
export const CreationGroupsJoin = ({
  rule,
  onChange
}: {
  rule?: Rule | null;
  onChange: (rule: Rule) => void;
}) => {
  const view = viewOf(rule, Kind.And);
  if (view.type !== 'multi' || view.groups.length < 2 || !rule) return null;
  return (
    <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
      <span>Groups must match</span>
      <Segments
        label="Groups must match"
        value={view.join}
        onChange={(join) => onChange({ ...rule, kind: join })}
      />
    </div>
  );
};

export const CreationRuleEditor = ({
  rule,
  options,
  errors,
  onChange
}: {
  rule?: Rule | null;
  options: EmailBuilderOptions;
  errors: BuilderErrors;
  onChange: (rule: Rule | null) => void;
}) => {
  const [localMode, setLocalMode] = React.useState<Mode>(Kind.And);
  const view = viewOf(rule, localMode);
  const nodes = countNodes(rule);
  const fits = (added: number) => nodes + added <= MAX_NODES;
  const canAddPrompt = countPrompts(rule) < EMAIL_LIMITS.prompts;

  if (view.type === 'advanced')
    return (
      <div className="flex flex-col">
        <Help>This rule nests groups more deeply than the compact view shows. Edit it here.</Help>
        <RuleEditor
          node={rule!}
          totalPrompts={countPrompts(rule)}
          path={RULE_PATH}
          options={options}
          errors={errors}
          totalNodes={nodes}
          onChange={onChange}
          onRemove={() => onChange(null)}
        />
      </div>
    );

  const groups =
    view.type === 'empty'
      ? [{ mode: localMode, rows: [], path: RULE_PATH, node: null } as Group]
      : view.type === 'single'
        ? [view.group]
        : view.groups;
  const multi = view.type === 'multi';

  // Rebuild the tree after a group's rows or mode changed. A group left without
  // rows is removed (unless keepEmpty, used when only its mode changes); a
  // single remaining group becomes the root again.
  const setGroup = (index: number, mode: Mode, rows: Rule[], keepEmpty = false) => {
    if (view.type === 'multi') {
      const children = view.groups
        .map((item, i) =>
          i === index ? { ...item.node!, kind: mode, children: rows } : item.node!
        )
        .filter((_, i) => i !== index || rows.length || keepEmpty);
      onChange(children.length > 1 ? { ...rule!, children } : (children[0] ?? null));
      return;
    }
    const current = groups[0];
    if (current.node)
      return onChange(rows.length ? { ...current.node, kind: mode, children: rows } : null);
    // No group node yet: the mode is kept locally until a second condition needs it.
    setLocalMode(mode);
    if (!rows.length) return view.type === 'empty' ? undefined : onChange(null);
    if (rows.length === 1 && rows[0] === current.rows[0]?.node) return;
    onChange(rows.length === 1 ? rows[0] : group(mode, rows));
  };
  const addGroup = () => {
    if (view.type === 'multi')
      return onChange({ ...rule!, children: [...rule!.children!, newRule(Kind.And)] });
    const first =
      view.type === 'empty'
        ? newRule(localMode)
        : (view.group.node ??
          group(
            localMode,
            view.group.rows.map((row) => row.node)
          ));
    onChange(group(Kind.And, [first, newRule(Kind.And)]));
  };
  const groupCost = view.type === 'multi' ? 2 : view.type === 'empty' ? 5 : groups[0].node ? 3 : 4;

  return (
    <div className="flex flex-col">
      {(view.type !== 'single' || view.group.node) && (
        <PathErrors path={RULE_PATH} errors={errors} />
      )}
      {groups.map((item, index) => {
        const rows = item.rows.map((row) => row.node);
        const addCost = item.node || !item.rows.length ? 1 : 2;
        return (
          <React.Fragment key={item.node?.id ?? 'root'}>
            {multi && index > 0 && (
              <div className="my-2.5 flex items-center gap-2.5" aria-hidden="true">
                <span className="h-px flex-1 bg-neutral-200 dark:bg-neutral-700" />
                <Badge color="sky">
                  {view.type === 'multi' && view.join === Kind.Or ? 'OR' : 'AND'}
                </Badge>
                <span className="h-px flex-1 bg-neutral-200 dark:bg-neutral-700" />
              </div>
            )}
            <div
              className="rounded-lg border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-700 dark:bg-neutral-900"
              role="group"
              aria-label={multi ? `Condition group ${index + 1}` : 'Conditions'}
            >
              <div className="mb-3 flex flex-wrap items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
                <span>Match</span>
                <Segments
                  label={multi ? `Group ${index + 1} must match` : 'Conditions must match'}
                  value={item.mode}
                  onChange={(mode) => setGroup(index, mode, rows, true)}
                />
                <span>of these conditions</span>
                {multi && (
                  <Button
                    plain
                    className="ml-auto"
                    aria-label="Remove group"
                    onClick={() => setGroup(index, item.mode, [])}
                  >
                    <TrashIcon />
                  </Button>
                )}
              </div>
              {item.node && item.path !== RULE_PATH && (
                <PathErrors path={item.path} errors={errors} />
              )}
              {item.rows.length ? (
                <div className="flex flex-col gap-2">
                  {item.rows.map((row, i) => (
                    <Condition
                      key={row.leaf.id}
                      row={row}
                      options={options}
                      errors={errors}
                      canNegate={fits(1)}
                      canAddPrompt={canAddPrompt}
                      onChange={(node) =>
                        setGroup(
                          index,
                          item.mode,
                          rows.map((old, j) => (j === i ? node : old))
                        )
                      }
                      onRemove={() =>
                        setGroup(
                          index,
                          item.mode,
                          rows.filter((_, j) => j !== i)
                        )
                      }
                    />
                  ))}
                </div>
              ) : (
                <div className="py-1.5 text-sm text-neutral-500 dark:text-neutral-400">
                  {view.type === 'empty'
                    ? 'No conditions. Matching arrivals within the chosen mailbox scope are eligible.'
                    : 'Add a condition. An empty group cannot be saved.'}
                </div>
              )}
              <Button
                plain
                className="mt-2"
                disabled={!fits(addCost)}
                onClick={() => setGroup(index, item.mode, [...rows, newRule(Kind.Phrase)])}
              >
                <PlusIcon />
                Add condition
              </Button>
            </div>
          </React.Fragment>
        );
      })}
      <Button
        color="white"
        className="mt-4 self-start"
        disabled={!fits(groupCost)}
        onClick={addGroup}
      >
        <PlusIcon />
        Add group
      </Button>
      {nodes >= MAX_NODES && (
        <Help>This trigger uses all 128 conditions and groups. Remove one to add another.</Help>
      )}
    </div>
  );
};
